import moment from "moment";
import * as XLSX from "xlsx";

export function formatDate(dateStr) {
  return new Intl.DateTimeFormat("en", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(dateStr));
}

  // Format the date for display
  export function newFormatDate(dateString) {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

export function objectToFormData(obj) {
  const formData = new FormData();

  // Iterate through the object's keys
  for (let key in obj) {
    // Check if the key is a property of the object (not inherited)
    if (obj.hasOwnProperty(key)) {
      formData.append(key, obj[key]);
    }
  }
  return formData;
}

export function truncateText(text, maxLength) {
  // if (!text) {
  //   return "";
  // }
  if (text.length > maxLength) {
    return text.substring(0, maxLength) + "...";
  } else {
    return text;
  }
}

export function createAvatarName(username) {
  return username
    ?.split(" ")
    ?.map((el, i) => (i < 2 ? el[0] : ""))
    ?.join("");
}

export const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

export const differenceInDays = (date) => {
  const date1 = moment(date);
  const date2 = moment();

  // Calculate the difference in days
  return date2.diff(date1, "days");
};

export const differenceInDaysOrHours = (date) => {
  const date1 = moment.utc(date).local();
  const date2 = moment();
  if (date == null) {
    return `not seen yet`;
  }
  const diffInMinutes = date2.diff(date1, "minutes");
  if (diffInMinutes < 1) {
    return `just now`;
  }
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minutes ago`;
  }
  // Calculate the difference in hours
  const diffInHours = date2.diff(date1, "hours");
  if (diffInHours < 24) {
    return `${diffInHours} hours ago`;
  } else {
    // Calculate the difference in days
    const diffInDays = date2.diff(date1, "days");
    return `${diffInDays} days ago`;
  }
};

export const hasImage = (imagePath) => {
  if (!imagePath) {
    return false;
  }
  // Define a regular expression to match common image file extensions
  const imageExtensions = /\.(jpg|jpeg|png|gif|bmp|webp|svg|tiff|ico)$/i;

  // Test if the imagePath ends with one of the image file extensions
  return imageExtensions.test(imagePath);
};

export const compareDates = (dateTime1, dateTime2) => {
  // Extract the date part (YYYY-MM-DD) from the datetime strings
  const date1 = dateTime1.split(" ")[0];
  const date2 = dateTime2.split(" ")[0];

  // Compare the dates and return true if they are equal, otherwise false
  return date1 === date2;
};

export const getInitials = (first_name, last_name) => {
  // const { first_name, last_name } = data;

  // Check if first_name and last_name are provided and not empty
  if (first_name && last_name) {
    const firstInitial = first_name.charAt(0).toUpperCase();
    const lastInitial = last_name.charAt(0).toUpperCase();

    return `${firstInitial}${lastInitial}`;
  } else {
    return "";
  }
};

export const relativeTime = (date) => {
  if (!date) return "Pending";
  const localDate = moment.utc(date).local();
  return localDate.fromNow();
};

export function removeHtmlTagsAndEntities(str) {
  const htmlTagPattern = /<[^>]*>/g;
  const htmlEntityPattern = /&[^;]+;/g;

  return str?.replace(htmlTagPattern, "")?.replace(htmlEntityPattern, "");
}

export const formatTimeFromNow = (createdDate) => {
  if (!createdDate) return "";

  const newDate = moment.utc(createdDate);
  const localDate = newDate?.local().toDate();
  const now = moment();

  const differences = {
    minutes: now.diff(localDate, "minutes"),
    hours: now.diff(localDate, "hours"),
    days: now.diff(localDate, "days"),
    months: now.diff(localDate, "months"),
    years: now.diff(localDate, "years"),
  };

  const { minutes, hours, days, months, years } = differences;

  const formatUnit = (value, unit) =>
    `${value} ${unit}${value === 1 ? "" : "s"} ago`;

  if (minutes === 0) return "just now";
  if (minutes < 60) return formatUnit(minutes, "Min");
  if (hours < 24) return formatUnit(hours, "Hr");
  if (days <= 31) return formatUnit(days, "Day");
  if (days < 365) return formatUnit(months, "Month");
  return formatUnit(years, "Year");
};

export const getLastSevenDays = () => {
  // Get today's date and the date 7 days ago
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 7);

  const { from, days } = getDateRangeSearchParams({
    startDate,
    endDate,
  });

  return `from=${encodeURIComponent(from)}&days=${encodeURIComponent(days)}`;
};

export const parseLdapUrl = (url) => {
  const regex = /^(ldap[s]?:\/\/)?(?:\[([^\]]+)\]|([^:/?#]+))(?::(\d+))?/;
  const match = url.match(regex);

  if (!match) {
    throw new Error("Invalid LDAP URL");
  }

  const host = match[2] || match[3];
  const port = match[4];

  return { host, port };
};

export function fixedDigitsAfterDecimal(num) {
  if (num % 1 === 0) {
    return num;
  } else {
    return parseFloat(num?.toFixed(1));
  }
}

export const ReactQuillsProps = {
  modules: {
    toolbar: [
      [{ font: [] }],
      [{ size: [] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      ["blockquote", "code-block"],
      [{ align: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image", "video"],
    ],
    clipboard: {
      // toggle to add extra line breaks when pasting HTML:
      matchVisual: false,
    },
  },
  formats: [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "code-block",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
    "video",
    "align",
    "color",
    "background",
  ],
};

export const generateCSV = (data, filename = "test") => {
  const worksheet = XLSX.utils.json_to_sheet(data); // Convert JSON data to worksheet
  const workbook = XLSX.utils.book_new(); // Create a new workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1"); // Append the worksheet
  XLSX.writeFile(workbook, `${filename}.xlsx`); // Export the workbook to an Excel file
};

export const splitString = (date) => {
  if (date) return date.split("T");
};

export function changeDateFormatToMonthDayYear(dateStr) {
  // Split the date string by '/'
  const [year, month, day] = dateStr.split("/");

  // Rearrange the date components to 'YYYY-MM-DD' format
  return `${month}/${day}/${year}`;
}
export const formatDateMMDDYY = (str) => {
  if (!str) return '';

  return moment(str).format('MM/DD/YYYY');
}

export const parseValueToArrayOrNone = (value) => {
  try {
    if (typeof value === 'string') {
      if (value.trim() === '') {
        return 'None';
      }
      if (value.includes(',')) {
        return value.split(',').map(item => item.trim());
      }
      return [value.trim()];
    }
    const parsedValue = JSON.parse(value);
    if (Array.isArray(parsedValue)) {
      return parsedValue.length > 0 ? parsedValue : 'None';
    }
    return parsedValue || 'None';
  } catch (e) {
    return value || 'None';
  }
};

const DATE_RANGE_INPUT_FORMATS = ["M-D-YYYY", "MM-DD-YYYY", "M/D/YYYY", "MM/DD/YYYY"];

function parseDateRangeValue(dateValue) {
  return moment(dateValue, DATE_RANGE_INPUT_FORMATS, true);
}

export function convertStartEndDateToFromTo(startDate, endDate) {
  const parsedStartDate = parseDateRangeValue(startDate);
  const parsedEndDate = parseDateRangeValue(endDate);

  if (!parsedStartDate.isValid() || !parsedEndDate.isValid()) {
    throw new Error("Invalid startDate or endDate");
  }

  return {
    from: parsedStartDate.format("M-D-YYYY"),
    days: parsedEndDate.diff(parsedStartDate, "days"),
  };
}

export function convertFromToToStartEndDate(from, days) {
  const parsedFromDate = parseDateRangeValue(from);
  const parsedDaysValue = Number(days);

  if (!parsedFromDate.isValid()) {
    throw new Error("Invalid from date");
  }

  if (!Number.isInteger(parsedDaysValue) || parsedDaysValue < 0) {
    throw new Error("Invalid days value");
  }

  return {
    startDate: parsedFromDate.format("M-D-YYYY"),
    endDate: parsedFromDate.clone().add(parsedDaysValue, "days").format("M-D-YYYY"),
  };
}

export function serializeDateRangeToFromTo(dateRange) {
  if (!dateRange?.startDate || !dateRange?.endDate) {
    return "";
  }

  return JSON.stringify(
    convertStartEndDateToFromTo(
      moment(dateRange.startDate).format("M-D-YYYY"),
      moment(dateRange.endDate).format("M-D-YYYY")
    )
  );
}

export function getDateRangeSearchParams(dateRange) {
  if (!dateRange?.startDate || !dateRange?.endDate) {
    return {
      from: "",
      days: "",
    };
  }

  return convertStartEndDateToFromTo(
    moment(dateRange.startDate).format("M-D-YYYY"),
    moment(dateRange.endDate).format("M-D-YYYY")
  );
}

export function parseDateRangeSearchParam(dateParam) {
  if (!dateParam) {
    return null;
  }

  const parsedDate =
    typeof dateParam === "string" ? JSON.parse(dateParam) : dateParam;

  if (
    parsedDate?.from &&
    (parsedDate?.days !== undefined || parsedDate?.to !== undefined)
  ) {
    const normalizedDateRange = convertFromToToStartEndDate(
      parsedDate.from,
      parsedDate.days ?? parsedDate.to
    );

    return {
      startDate: new Date(normalizedDateRange.startDate),
      endDate: new Date(normalizedDateRange.endDate),
      key: "selection",
    };
  }

  if (parsedDate?.startDate && parsedDate?.endDate) {
    return {
      startDate: new Date(parsedDate.startDate),
      endDate: new Date(parsedDate.endDate),
      key: "selection",
    };
  }

  return null;
}

export function parseDateRangeSearchParams({
  from,
  days,
  to,
  date,
} = {}) {
  const resolvedDays = days ?? to;

  if (
    from &&
    resolvedDays !== undefined &&
    resolvedDays !== null &&
    resolvedDays !== ""
  ) {
    return parseDateRangeSearchParam(
      JSON.stringify({
        from,
        days: Number(resolvedDays),
      })
    );
  }

  if (date) {
    return parseDateRangeSearchParam(date);
  }

  return null;
}
